
import { BlockStack, Text, Button, InlineStack, Icon, Card, IndexTable, DataTable, Modal, TextField, ButtonGroup, Tabs, Divider, Spinner } from "@shopify/polaris";
import { CashDollarIcon, DomainLandingPageIcon, PaymentIcon, PlusIcon, QuestionCircleIcon } from "@shopify/polaris-icons";
import { useQuery } from "@tanstack/react-query";
import { addDoc, collection, getDocs, where } from "firebase/firestore";
import { useState } from "react";
import AppLayout from "../components/layout";
import { usePartnerUp } from "../providers/PartnerUpStoreProvider";
import { numberToUSD } from "../utils";

export default function Conversions() {
  const { db, store, setToast } = usePartnerUp();
  const [editingConversion, setEditingConversion] = useState(null)
  const [installConversion, setInstallConversion] = useState(null)
  const [installPaymentConversion, setInstallPaymentConversion] = useState(null)
  const [paymentConversionType, setPaymentConversionType] = useState(0)
  const [addLoading, setAddLoading] = useState(false)

  const [notFetched, setNotFetched] = useState(true)

  const {
    data: conversions,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ['conversions'],
    queryFn: async () => {
      setNotFetched(false)
      try {
        const conversions = await getDocs(collection(db, 'conversions'), where('shop', '==', store?.id))
        return conversions.docs.map(doc => ({ ...doc.data(), id: doc.id }))
      } catch (e) {
        console.error(e)
        setToast({ content: 'Error fetching conversions', isError: true })
      }
    },
    enabled: !!store?.id
  })

  async function addConversion() {
    try {
      setAddLoading(true)
      await addDoc(collection(db, 'conversions'), {
        shop: store.id,
        name: editingConversion.name,
        type: editingConversion.type,
        defaultValue: editingConversion.defaultValue || null,
      })
      refetch()
      setToast({ content: 'Conversion added!' })
      setEditingConversion(null)
    } catch (e) {
      console.error(e)
      setToast({ content: 'Error adding conversion', isError: true })
    } finally {
      setAddLoading(false)
    }
  }

  const webConversions = conversions?.filter(c => c.type === 'web') || [];
  const paymentConversions = conversions?.filter(c => c.type === 'payment') || [];

  return (
    <AppLayout>
      <Modal size="small" title={editingConversion?.type === 'web' ? 'Web Event' : 'Conversion Event'} open={editingConversion} onClose={() => setEditingConversion(null)}
        primaryAction={{
          content: 'Save',
          loading: addLoading,
          onAction: addConversion
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setEditingConversion(null)
            }
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <TextField label="Name" value={editingConversion?.name} onChange={v => {
              setEditingConversion({ ...editingConversion, name: v })
            }} />
            {
              editingConversion?.type === 'payment' && (
                <TextField label="Default Value" type="number" prefix="$" value={editingConversion?.defaultValue} onChange={v => {
                  setEditingConversion({ ...editingConversion, defaultValue: v })
                }} />
              )
            }
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Modal size="large" title={`Install ${installConversion?.name}`} open={installConversion} onClose={() => setInstallConversion(null)}
        primaryAction={{
          content: 'Done',
          onAction: () => {
            setInstallConversion(null)
          }
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setInstallConversion(null)
            }
          }
        ]}
      >
        <Modal.Section>
          <Text>Make sure the universal code is installed in the head of all pages</Text>
          <div style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '5px' }}>
            <pre>
              {
                `<script>
var discountQs = new URLSearchParams(window.location.search).get('discount')
document.cookie = 'prup=' + discountQs
</script>`
              }
            </pre>
          </div>
          <Text>Paste this code on the correct page</Text>
          <div style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '5px' }}>
            <pre>
              {
                `<script>
  var cookie = document.cookie.match(/prup=([^;]+)/)
  fetch('https://prup.io/api/conversions/${installConversion?.id}?code=' + cookie[1])
</script>`
              }
            </pre>
          </div>
        </Modal.Section>
      </Modal>
      <Modal size="large" title={`Install ${installPaymentConversion?.name}`} open={installPaymentConversion} onClose={() => setInstallPaymentConversion(null)}
        primaryAction={{
          content: 'Done',
          onAction: () => {
            setInstallPaymentConversion(null)
          }
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setInstallPaymentConversion(null)
            }
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Tabs tabs={[
              { content: 'Manual Install', id: 'manual' },
              { content: 'Integration', id: 'integration' },
            ]} selected={paymentConversionType} onSelect={v => setPaymentConversionType(v)} />
            <Divider />
            <Text>Create an API Secret Key</Text>
            <InlineStack>
              <Button>Generate</Button>
            </InlineStack>
            <Text>Send a Server Request</Text>
            <div style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '5px' }}>
              <pre>
                {
                  `POST https://prup.io/api/payment-conversions/${installPaymentConversion?.id}

Authorization
Bearer {{API_SECRET}}

Body 
{
  id: {{PAYMENT_ID}},
  code: {{DISCOUNT_CODE}},
  value: {{ORDER_VALUE}} (optional)
}
`
                }
              </pre>
            </div>
          </BlockStack>
        </Modal.Section>
      </Modal>
      <BlockStack gap="500">
        <InlineStack align="space-between" gap="300" blockAlign="start">
          <BlockStack>
            <InlineStack gap="200" blockAlign="center" align="start">
              <InlineStack>
                <Icon source={CashDollarIcon} />
              </InlineStack>
              <Text variant="headingXl">Conversions</Text>
            </InlineStack>
            <InlineStack gap="100" blockAlign="center">
              <Text>Track Creator Performance</Text>
              <Button size="micro" plain icon={QuestionCircleIcon} external url="https://partnerup.helpscoutdocs.com/article/19-measuring-performance" />
            </InlineStack>
          </BlockStack>
        </InlineStack>
        <BlockStack gap="500">
          <BlockStack gap="300">
            <InlineStack align="space-between">
              <InlineStack gap="200" blockAlign="center" align="start">
                <InlineStack>
                  <Icon source={DomainLandingPageIcon} />
                </InlineStack>
                <Text variant="headingLg">Web Events</Text>
              </InlineStack>
              <InlineStack gap="300">
                <Button size="large" primary icon={PlusIcon} onClick={() => {
                  setEditingConversion({ name: '' })
                }}>New Event</Button>
              </InlineStack>
            </InlineStack>
            {
              (isLoading || notFetched) ? <Spinner /> : webConversions?.length ? (
                <DataTable headings={['Label', 'ID', 'Events', 'Default Value', 'Actions']} columnContentTypes={['text', 'text', 'text', 'text']} rows={
                  webConversions.map(c => [
                    c.name,
                    c.id,
                    c.events?.length || 0,
                    '-',
                    <ButtonGroup>
                      <Button plain onClick={() => setInstallConversion(c)}>Install</Button>
                      <Button plain onClick={() => setEditingConversion(c)}>Edit</Button>
                    </ButtonGroup>
                  ])
                } />
              ) : (
                <Card>
                  <BlockStack gap="300" inlineAlign="center">
                    <Text variant="headingMd">No Web Events Yet</Text>
                    <Button size="large" primary icon={PlusIcon} onClick={() => {
                      setEditingConversion({ name: '', type: 'web' })
                    }}>Add Web Event</Button>
                  </BlockStack>
                </Card>
              )
            }
          </BlockStack>
          <BlockStack gap="300">
            <InlineStack align="space-between">
              <InlineStack gap="200" blockAlign="center" align="start">
                <InlineStack>
                  <Icon source={PaymentIcon} />
                </InlineStack>
                <Text variant="headingLg">Payment Conversions</Text>
              </InlineStack>
              <InlineStack gap="300">
                <Button size="large" primary icon={PlusIcon}>New Conversion</Button>
              </InlineStack>
            </InlineStack>
            {
              (isLoading || notFetched) ? <Spinner /> : paymentConversions?.length ? (
                <DataTable headings={['Label', 'ID', 'Events', 'Default Value', 'Actions']} columnContentTypes={['text', 'text', 'text']} rows={
                  paymentConversions.map(c => [
                    c.name,
                    c.id,
                    c.events?.length || 0,
                    numberToUSD(c.defaultValue),
                    <ButtonGroup>
                      <Button plain onClick={() => setInstallPaymentConversion(c)}>Install</Button>
                      <Button plain>Edit</Button>
                    </ButtonGroup>
                  ])
                } />
              ) : (
                <Card>
                  <BlockStack gap="300" inlineAlign="center">
                    <Text variant="headingMd">No Payment Conversions Yet</Text>
                    <Button size="large" primary icon={PlusIcon} onClick={() => {
                      setEditingConversion({ name: '', type: 'payment' })
                    }}>Add Conversion</Button>
                  </BlockStack>
                </Card>
              )
            }
          </BlockStack>
        </BlockStack>
      </BlockStack>
    </AppLayout>
  )
}
